/**
 * Formats text to render markdown syntax properly
 * Supports: headings, bold, italic, code blocks, inline code, lists, links, blockquotes
 */
export const formatTextWithCode = (text) => {
  if (!text) return text;

  // Convert to string if not already
  const textStr = String(text);

  // Parse markdown into structured parts
  const lines = textStr.split('\n');
  const parts = [];
  let currentList = null;
  let inCodeBlock = false;
  let codeBlockContent = '';
  let codeBlockLanguage = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Handle code blocks
    if (line.trim().startsWith('```')) {
      if (!inCodeBlock) {
        // Start code block
        inCodeBlock = true;
        codeBlockLanguage = line.trim().substring(3).trim();
        codeBlockContent = '';
      } else {
        // End code block
        inCodeBlock = false;
        parts.push({
          type: 'codeBlock',
          language: codeBlockLanguage || 'plaintext',
          content: codeBlockContent.trim()
        });
        codeBlockContent = '';
        codeBlockLanguage = '';
      }
      continue;
    }

    // If inside code block, accumulate content
    if (inCodeBlock) {
      codeBlockContent += (codeBlockContent ? '\n' : '') + line;
      continue;
    }

    // Close current list if line doesn't start with list marker
    if (currentList && !line.trim().match(/^(\*|-|\d+\.)\s/)) {
      parts.push(currentList);
      currentList = null;
    }

    // Empty line
    if (line.trim() === '') {
      parts.push({ type: 'lineBreak' });
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      parts.push({
        type: 'heading',
        level: level,
        content: headingMatch[2]
      });
      continue;
    }

    // Blockquote
    if (line.trim().startsWith('>')) {
      parts.push({
        type: 'blockquote',
        content: line.trim().substring(1).trim()
      });
      continue;
    }

    // Lists (unordered)
    const unorderedListMatch = line.match(/^(\s*)(\*|-)\s+(.+)$/);
    if (unorderedListMatch) {
      const indent = unorderedListMatch[1].length;
      if (!currentList || currentList.listType !== 'unordered') {
        if (currentList) {
          parts.push(currentList);
        }
        currentList = {
          type: 'list',
          listType: 'unordered',
          items: []
        };
      }
      currentList.items.push({
        content: unorderedListMatch[3],
        indent: Math.floor(indent / 2)
      });
      continue;
    }

    // Lists (ordered)
    const orderedListMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (orderedListMatch) {
      const indent = orderedListMatch[1].length;
      if (!currentList || currentList.listType !== 'ordered') {
        if (currentList) {
          parts.push(currentList);
        }
        currentList = {
          type: 'list',
          listType: 'ordered',
          items: []
        };
      }
      currentList.items.push({
        content: orderedListMatch[3],
        indent: Math.floor(indent / 2),
        number: parseInt(orderedListMatch[2])
      });
      continue;
    }

    // Regular text
    parts.push({
      type: 'text',
      content: line
    });
  }

  // Close any open list
  if (currentList) {
    parts.push(currentList);
  }

  return parts;
};

/**
 * Process inline markdown (bold, italic, links, inline code)
 */
const processInlineMarkdown = (text) => {
  if (!text) return [{ type: 'plain', content: '' }];

  const segments = [];
  let remaining = text;
  let position = 0;

  // Patterns for inline markdown (order matters!)
  const patterns = [
    { 
      // Code first (to avoid conflicts)
      regex: /`([^`]+)`/,
      type: 'inlineCode',
      extract: (match) => match[1]
    },
    { 
      // Bold (** or __)
      regex: /(\*\*|__)([^\*_]+)\1/,
      type: 'bold',
      extract: (match) => match[2]
    },
    { 
      // Italic (* or _)
      regex: /(\*|_)([^\*_]+)\1/,
      type: 'italic',
      extract: (match) => match[2]
    },
    { 
      // Links [text](url)
      regex: /\[([^\]]+)\]\(([^\)]+)\)/,
      type: 'link',
      extract: (match) => ({ text: match[1], url: match[2] })
    }
  ];

  while (remaining.length > 0) {
    let earliestMatch = null;
    let earliestPattern = null;

    // Find the earliest match
    for (const pattern of patterns) {
      const match = remaining.match(pattern.regex);
      if (match && (earliestMatch === null || match.index < earliestMatch.index)) {
        earliestMatch = match;
        earliestPattern = pattern;
      }
    }

    if (!earliestMatch) {
      // No more matches, add remaining text
      if (remaining) {
        segments.push({ type: 'plain', content: remaining });
      }
      break;
    }

    // Add text before match
    if (earliestMatch.index > 0) {
      segments.push({ 
        type: 'plain', 
        content: remaining.substring(0, earliestMatch.index) 
      });
    }

    // Add matched segment
    segments.push({
      type: earliestPattern.type,
      content: earliestPattern.extract(earliestMatch)
    });

    // Update remaining text
    remaining = remaining.substring(earliestMatch.index + earliestMatch[0].length);
  }

  return segments.length > 0 ? segments : [{ type: 'plain', content: text }];
};

/**
 * Simple syntax highlighter for code blocks
 */
const highlightCode = (code, language) => {
  if (!code) return code;
  
  const lang = (language || 'plaintext').toLowerCase();
  
  // Define syntax patterns for different languages
  const patterns = {
    javascript: [
      { regex: /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, style: { color: '#6a9955' } }, // Comments
      { regex: /\b(const|let|var|function|return|if|else|for|while|class|import|export|from|async|await|try|catch|new|this|super|extends|static|get|set|break|continue|switch|case|default|throw|typeof|instanceof|delete|void|yield|debugger|with|in|of)\b/g, style: { color: '#569cd6' } }, // Keywords
      { regex: /\b(true|false|null|undefined|NaN|Infinity)\b/g, style: { color: '#569cd6' } }, // Constants
      { regex: /(['"`])(?:(?=(\\?))\2.)*?\1/g, style: { color: '#ce9178' } }, // Strings
      { regex: /\b\d+\.?\d*\b/g, style: { color: '#b5cea8' } }, // Numbers
      { regex: /\b([A-Z][a-zA-Z0-9]*)\b/g, style: { color: '#4ec9b0' } }, // Classes/Types
      { regex: /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g, style: { color: '#dcdcaa' }, group: 1 } // Functions
    ],
    python: [
      { regex: /(#.*$)/gm, style: { color: '#6a9955' } }, // Comments
      { regex: /\b(def|class|import|from|as|if|elif|else|for|while|return|try|except|finally|with|lambda|yield|async|await|pass|break|continue|raise|assert|del|global|nonlocal|in|is|and|or|not)\b/g, style: { color: '#569cd6' } }, // Keywords
      { regex: /\b(True|False|None)\b/g, style: { color: '#569cd6' } }, // Constants
      { regex: /('''[\s\S]*?'''|"""[\s\S]*?"""|'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")/g, style: { color: '#ce9178' } }, // Strings
      { regex: /\b\d+\.?\d*\b/g, style: { color: '#b5cea8' } }, // Numbers
      { regex: /\b([A-Z][a-zA-Z0-9]*)\b/g, style: { color: '#4ec9b0' } }, // Classes
      { regex: /([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g, style: { color: '#dcdcaa' }, group: 1 } // Functions
    ],
    java: [
      { regex: /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, style: { color: '#6a9955' } }, // Comments
      { regex: /\b(public|private|protected|static|final|abstract|class|interface|extends|implements|import|package|new|return|if|else|for|while|do|switch|case|default|break|continue|try|catch|finally|throw|throws|synchronized|volatile|transient|native|strictfp|super|this|void|boolean|byte|char|short|int|long|float|double)\b/g, style: { color: '#569cd6' } }, // Keywords
      { regex: /\b(true|false|null)\b/g, style: { color: '#569cd6' } }, // Constants
      { regex: /"(?:[^"\\]|\\.)*"/g, style: { color: '#ce9178' } }, // Strings
      { regex: /\b\d+\.?\d*[fFdDlL]?\b/g, style: { color: '#b5cea8' } }, // Numbers
      { regex: /\b([A-Z][a-zA-Z0-9]*)\b/g, style: { color: '#4ec9b0' } }, // Classes/Types
      { regex: /([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g, style: { color: '#dcdcaa' }, group: 1 } // Functions
    ],
    cpp: [
      { regex: /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, style: { color: '#6a9955' } }, // Comments
      { regex: /\b(auto|break|case|char|const|continue|default|do|double|else|enum|extern|float|for|goto|if|inline|int|long|register|restrict|return|short|signed|sizeof|static|struct|switch|typedef|union|unsigned|void|volatile|while|bool|class|private|protected|public|namespace|using|virtual|override|final|nullptr|constexpr|template|typename)\b/g, style: { color: '#569cd6' } }, // Keywords
      { regex: /\b(true|false|nullptr|NULL)\b/g, style: { color: '#569cd6' } }, // Constants
      { regex: /"(?:[^"\\]|\\.)*"/g, style: { color: '#ce9178' } }, // Strings
      { regex: /\b\d+\.?\d*[fFlLuU]?\b/g, style: { color: '#b5cea8' } }, // Numbers
      { regex: /\b([A-Z][a-zA-Z0-9]*)\b/g, style: { color: '#4ec9b0' } }, // Classes/Types
      { regex: /([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g, style: { color: '#dcdcaa' }, group: 1 } // Functions
    ],
    c: [
      { regex: /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm, style: { color: '#6a9955' } }, // Comments
      { regex: /\b(auto|break|case|char|const|continue|default|do|double|else|enum|extern|float|for|goto|if|inline|int|long|register|restrict|return|short|signed|sizeof|static|struct|switch|typedef|union|unsigned|void|volatile|while)\b/g, style: { color: '#569cd6' } }, // Keywords
      { regex: /\b(true|false|NULL)\b/g, style: { color: '#569cd6' } }, // Constants
      { regex: /"(?:[^"\\]|\\.)*"/g, style: { color: '#ce9178' } }, // Strings
      { regex: /\b\d+\.?\d*[fFlLuU]?\b/g, style: { color: '#b5cea8' } }, // Numbers
      { regex: /([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g, style: { color: '#dcdcaa' }, group: 1 } // Functions
    ],
    html: [
      { regex: /(&lt;!--[\s\S]*?--&gt;|<!--[\s\S]*?-->)/g, style: { color: '#6a9955' } }, // Comments
      { regex: /(&lt;\/?[\w-]+)/g, style: { color: '#569cd6' } }, // Tags
      { regex: /(<\/?[\w-]+)/g, style: { color: '#569cd6' } }, // Tags
      { regex: /\s([\w-]+)=/g, style: { color: '#9cdcfe' }, group: 1 }, // Attributes
      { regex: /="([^"]*)"/g, style: { color: '#ce9178' } }, // Attribute values
      { regex: /='([^']*)'/g, style: { color: '#ce9178' } } // Attribute values
    ],
    css: [
      { regex: /(\/\*[\s\S]*?\*\/)/g, style: { color: '#6a9955' } }, // Comments
      { regex: /([.#]?[\w-]+)\s*\{/g, style: { color: '#d7ba7d' }, group: 1 }, // Selectors
      { regex: /([\w-]+):/g, style: { color: '#9cdcfe' }, group: 1 }, // Properties
      { regex: /:\s*([^;}\n]+)/g, style: { color: '#ce9178' }, group: 1 } // Values
    ],
    sql: [
      { regex: /(--.*$)/gm, style: { color: '#6a9955' } }, // Comments
      { regex: /\b(SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|TABLE|DATABASE|INDEX|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AND|OR|NOT|NULL|PRIMARY|KEY|FOREIGN|REFERENCES|AS|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|UNION|DISTINCT|COUNT|SUM|AVG|MAX|MIN|LIKE|IN|BETWEEN|EXISTS|CASE|WHEN|THEN|ELSE|END)\b/gi, style: { color: '#569cd6' } }, // Keywords
      { regex: /\b(INT|VARCHAR|TEXT|DATE|DATETIME|BOOLEAN|FLOAT|DOUBLE|DECIMAL|CHAR|BLOB)\b/gi, style: { color: '#4ec9b0' } }, // Types
      { regex: /'(?:[^'\\]|\\.)*'/g, style: { color: '#ce9178' } }, // Strings
      { regex: /\b\d+\.?\d*\b/g, style: { color: '#b5cea8' } } // Numbers
    ]
  };

  const langPatterns = patterns[lang] || [];
  if (langPatterns.length === 0) {
    return [{ text: code, style: {} }];
  }

  // Track which characters have been matched to avoid overlapping
  const segments = [{ text: code, style: {}, start: 0, end: code.length }];
  
  langPatterns.forEach(pattern => {
    const newSegments = [];
    
    segments.forEach(segment => {
      if (Object.keys(segment.style).length > 0) {
        // Already styled, keep as is
        newSegments.push(segment);
        return;
      }

      let lastIndex = 0;
      const matches = [];
      let match;
      
      while ((match = pattern.regex.exec(segment.text)) !== null) {
        const matchText = pattern.group ? match[pattern.group] : match[0];
        const matchIndex = pattern.group ? segment.text.indexOf(matchText, match.index) : match.index;
        
        matches.push({
          index: matchIndex,
          text: matchText,
          style: pattern.style
        });
      }

      if (matches.length === 0) {
        newSegments.push(segment);
        return;
      }

      matches.forEach((m, i) => {
        // Add unstyled text before match
        if (m.index > lastIndex) {
          newSegments.push({
            text: segment.text.substring(lastIndex, m.index),
            style: {}
          });
        }
        
        // Add styled match
        newSegments.push({
          text: m.text,
          style: m.style
        });
        
        lastIndex = m.index + m.text.length;
      });

      // Add remaining unstyled text
      if (lastIndex < segment.text.length) {
        newSegments.push({
          text: segment.text.substring(lastIndex),
          style: {}
        });
      }
    });

    segments.length = 0;
    segments.push(...newSegments);
  });

  return segments;
};

/**
 * React component to render formatted text with markdown
 */
export const FormattedText = ({ text, style = {} }) => {
  const parts = formatTextWithCode(text);

  // Language display names and colors
  const languageInfo = {
    javascript: { name: 'JavaScript', color: '#f7df1e', bg: '#1e1e1e' },
    python: { name: 'Python', color: '#3776ab', bg: '#1e1e1e' },
    java: { name: 'Java', color: '#007396', bg: '#1e1e1e' },
    cpp: { name: 'C++', color: '#00599c', bg: '#1e1e1e' },
    c: { name: 'C', color: '#a8b9cc', bg: '#1e1e1e' },
    html: { name: 'HTML', color: '#e34c26', bg: '#1e1e1e' },
    css: { name: 'CSS', color: '#264de4', bg: '#1e1e1e' },
    sql: { name: 'SQL', color: '#00758f', bg: '#1e1e1e' },
    plaintext: { name: 'Code', color: '#a0aec0', bg: '#1e1e1e' }
  };

  // Render inline markdown segments
  const renderInlineSegments = (segments) => {
    return segments.map((segment, idx) => {
      switch (segment.type) {
        case 'bold':
          return (
            <strong key={idx} style={{ fontWeight: 'bold' }}>
              {renderInlineSegments(processInlineMarkdown(segment.content))}
            </strong>
          );
        case 'italic':
          return (
            <em key={idx} style={{ fontStyle: 'italic' }}>
              {renderInlineSegments(processInlineMarkdown(segment.content))}
            </em>
          );
        case 'inlineCode':
          return (
            <code
              key={idx}
              style={{
                backgroundColor: '#edf2f7',
                color: '#c7254e',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '0.9em',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                border: '1px solid #e2e8f0'
              }}
            >
              {segment.content}
            </code>
          );
        case 'link':
          return (
            <a
              key={idx}
              href={segment.content.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#3182ce',
                textDecoration: 'underline'
              }}
            >
              {segment.content.text}
            </a>
          );
        case 'plain':
        default:
          return <span key={idx}>{segment.content}</span>;
      }
    });
  };

  return (
    <div style={style}>
      {parts.map((part, index) => {
        switch (part.type) {
          case 'heading':
            const HeadingTag = `h${part.level}`;
            const headingStyles = {
              1: { fontSize: '2em', fontWeight: 'bold', marginTop: '0.67em', marginBottom: '0.67em' },
              2: { fontSize: '1.5em', fontWeight: 'bold', marginTop: '0.75em', marginBottom: '0.75em' },
              3: { fontSize: '1.17em', fontWeight: 'bold', marginTop: '0.83em', marginBottom: '0.83em' },
              4: { fontSize: '1em', fontWeight: 'bold', marginTop: '1em', marginBottom: '1em' },
              5: { fontSize: '0.83em', fontWeight: 'bold', marginTop: '1.17em', marginBottom: '1.17em' },
              6: { fontSize: '0.67em', fontWeight: 'bold', marginTop: '1.33em', marginBottom: '1.33em' }
            };
            return (
              <HeadingTag key={index} style={headingStyles[part.level]}>
                {renderInlineSegments(processInlineMarkdown(part.content))}
              </HeadingTag>
            );

          case 'codeBlock':
            const lang = part.language || 'plaintext';
            const info = languageInfo[lang.toLowerCase()] || languageInfo.plaintext;
            
            return (
              <div
                key={index}
                style={{
                  margin: '12px 0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #4a5568'
                }}
              >
                {/* Language label */}
                <div
                  style={{
                    backgroundColor: '#1a202c',
                    color: info.color,
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #4a5568'
                  }}
                >
                  {info.name}
                </div>
                {/* Code content */}
                <pre
                  style={{
                    backgroundColor: info.bg,
                    color: '#d4d4d4',
                    padding: '16px',
                    margin: 0,
                    overflow: 'auto',
                    fontSize: '13px',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  <code>
                    {highlightCode(part.content, part.language).map((segment, segIdx) => (
                      <span key={segIdx} style={segment.style}>
                        {segment.text}
                      </span>
                    ))}
                  </code>
                </pre>
              </div>
            );

          case 'blockquote':
            return (
              <blockquote
                key={index}
                style={{
                  borderLeft: '4px solid #cbd5e0',
                  paddingLeft: '16px',
                  margin: '8px 0',
                  color: '#4a5568',
                  fontStyle: 'italic'
                }}
              >
                {renderInlineSegments(processInlineMarkdown(part.content))}
              </blockquote>
            );

          case 'list':
            const ListTag = part.listType === 'ordered' ? 'ol' : 'ul';
            return (
              <ListTag
                key={index}
                style={{
                  margin: '8px 0',
                  paddingLeft: '24px'
                }}
              >
                {part.items.map((item, itemIdx) => (
                  <li
                    key={itemIdx}
                    style={{
                      marginLeft: `${item.indent * 20}px`,
                      marginBottom: '4px'
                    }}
                  >
                    {renderInlineSegments(processInlineMarkdown(item.content))}
                  </li>
                ))}
              </ListTag>
            );

          case 'lineBreak':
            return <br key={index} />;

          case 'text':
            return (
              <p key={index} style={{ margin: '8px 0' }}>
                {renderInlineSegments(processInlineMarkdown(part.content))}
              </p>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};
